import { useState, useEffect, useContext, useRef } from "react";

import { useSearchParams } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";

import {
  SofaType,
  UserPreset,
  SofaPreset,
  CurrentElement,
  TypesData,
  MaterialsData,
  CurrentElements,
  ElementsData,
  UserTypes,
  CurPreset,
  SofaElementsUpdateAction,
  UrlStateParam,
} from "./DataTypes";
import { RestoreCurrentElement } from "../generated/GeneratedTypes";
import PresetChooser from "./PresetChooser";
import Sidebar from "./Sidebar";
import ElementDetails from "./ElementDetails";
import UserGuide from "./UserGuide";
import { client } from "../RpcClient";
import ActionBar from "./ActionBar";
import { config } from "../Config";
import { curOptionsToExternal } from "./Options";
import { Trans } from "../Translations";
import { eneIsLoading } from "../Util";
import ContactForm from "../ContactForm";
import { Space } from "../DataTypes";
import { vB } from "../Style";

const localStorageKey = "ree_feydom_user_sofa_types";
const localStorageKeyUserGuide = "ree_show_user_guide";

function getUserTypesFromLocalStorage(): UserTypes {
  return JSON.parse(window.localStorage.getItem(localStorageKey) ?? "{}");
}

export default function SofaIndex(props: {
  setLoading: (isLoading: boolean) => void;
  loadingLevel: boolean;
  space: Space;
}) {
  const { _, dynTr, gTxt } = useContext(Trans);
  const [params] = useSearchParams();
  const [elementsData, setElementsData] = useState<ElementsData>();
  const [typesData, setTypesData] = useState<TypesData>();
  const [curType, setCurType] = useState<number>(-1);
  const [curPresets, setCurPresets] = useState<{
    [key: number]: CurPreset;
  }>({});
  const [materialsData, setMaterialsData] = useState<MaterialsData>();
  const [curMaterial, setCurMaterial] = useState<number>(-1);
  const [curColors, setCurColors] = useState<{ [key: number]: number }>({});
  const [curElements, setCurElements] = useState<CurrentElements>({
    elements: [],
  });
  const [userTypes, setUserTypes] = useState<UserTypes>(
    getUserTypesFromLocalStorage()
  );
  const [pendingUserPresetDelete, setPendingUserPresetDelete] = useState<{
    preset: UserPreset;
    typeId: number;
  }>();
  const [roomSizeX, setRoomSizeX] = useState(0);
  const [roomSizeY, setRoomSizeY] = useState(0);
  const [roomHardWalls, setRoomHardWalls] = useState(false);
  const updateRoomDimensions = async () => {
    const roomSize = await client.request("SofaRoomSize", {});
    setRoomSizeX(Math.round(roomSize.x));
    setRoomSizeY(Math.round(roomSize.y));
    setRoomHardWalls(roomSize.hard_walls);
  };
  const [curOptions, setCurOptions] = useState<
    Array<{ groupId: number; optionId: number }>
  >([]);
  const [showingCurElements, setShowingCurElements] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const tutorialEnabled = false;
  const [showMeasures, setShowMeasures] = useState<boolean>(false);
  const [advanced3D, setAdvanced3D] = useState<boolean>(false);
  const [disableModuleMovements, setDisableModuleMovements] =
    useState<boolean>(false);
  const [contactFormOpen, setContactFormOpen] = useState<boolean>(false);
  const [myDesignsOpen, setMyDesignsOpen] = useState<boolean>(false);
  const [sofaChooserOpen, setSofaChooserOpen] = useState<boolean>(false);
  const [sofaChooserType, setSofaChooserType] = useState<number>(-1);
  const [sofaChooserDismissed, setSofaChooserDismissed] =
    useState<boolean>(false);
  const autoOpenedChooserRef = useRef(false);
  const fullScreenChooser = useMediaQuery("(max-width:900px)");
  const largerThen750 = useMediaQuery("(min-width:750px)");
  const isVbLg = useMediaQuery(vB.lg);
  const isXl = useMediaQuery("(min-width:1536px)");
  const topBarSmall = "48px";
  const topBarLarge = isXl ? "96px" : "72px";
  const chooseSofaButtonSx = {
    textTransform: "uppercase",
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#000",
    border: "2px solid #000",
    borderRadius: "7px",
    transition: "all 0.3s",
    boxShadow: "none",
    boxSizing: "border-box",
    outline: "none",
    "@media (orientation: landscape)": {
      xs: {
        height: "40px",
        fontSize: "10px",
        padding: "0 12px",
      },
      xl: {
        height: "60px",
        padding: "0 20px",
        fontSize: "14px",
      },
    },
    "@media (orientation: portrait)": {
      height: "40px",
      fontSize: "10px",
      padding: "0 12px",
    },
    ":hover": {
      backgroundColor: "#fff",
      color: "#000",
      borderColor: "#000",
      boxShadow: "none",
    },
    ":active": {
      backgroundColor: "#000",
      color: "#fff",
      borderColor: "#000",
      boxShadow: "none",
    },
    ":focus": {
      backgroundColor: "#000",
      color: "#fff",
      borderColor: "#000",
      boxShadow: "none",
    },
    ":focus:hover": {
      backgroundColor: "#fff",
      color: "#000",
      borderColor: "#000",
      boxShadow: "none",
    },
  };
  const backButtonSx = {
    textTransform: "none",
    fontFamily: '"Montserrat", sans-serif',
    fontSize: { xs: "0.7rem", lg: "0.9rem" },
    fontWeight: 300,
    border: "2px solid #d9d9d9",
    borderRadius: "3px",
    backgroundColor: "#fff",
    borderColor: "#d9d9d9",
    color: "#808080",
    padding: "6px 12px",
    minHeight: "36px",
    "@media (hover: hover)": {
      ":hover": {
        border: "2px solid #808080",
        backgroundColor: "#fff",
      },
    },
    "@media (hover: none)": {
      ":hover": {
        border: "2px solid #d9d9d9",
        backgroundColor: "#fff",
        borderColor: "#d9d9d9",
      },
    },
  };

  useEffect(() => {
    window.localStorage.setItem(localStorageKey, JSON.stringify(userTypes));
  }, [userTypes]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    (window as any).eneIsShareLink = params.has("state");
  }, []);
  useEffect(() => {
    if (!tutorialEnabled) {
      return;
    }
    const oldDate = window.localStorage.getItem(localStorageKeyUserGuide);
    if (oldDate !== null) {
      const oldTimestamp = Number(oldDate);
      if (Date.now() - oldTimestamp > 7 * 24 * 3600 * 1000) {
        setShowUserGuide(true);
      }
    } else {
      setShowUserGuide(true);
    }
    window.localStorage.setItem(
      localStorageKeyUserGuide,
      Date.now().toString()
    );
  }, [setShowUserGuide, tutorialEnabled]);
  useEffect(() => {
    if (!(window as any).eneIsShareLink && curType !== -1) {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);
      const hash = window.location.hash;
      const state: UrlStateParam = {
        elements: curElements.elements,
        type: curType,
        options: curOptions,
      };
      params.set("state", btoa(JSON.stringify(state)));
      window.history.replaceState(
        {},
        "",
        `${path}?${params.toString()}${hash}`
      );
    }
  }, [curElements, curType, curOptions]);
  useEffect(() => {
    if (typesData === undefined || (window as any).eneIsShareLink) {
      return;
    }
    if (
      curType === -1 &&
      !sofaChooserOpen &&
      !sofaChooserDismissed &&
      !autoOpenedChooserRef.current
    ) {
      autoOpenedChooserRef.current = true;
      setSofaChooserType(-1);
      setSofaChooserOpen(true);
    }
    if (curType !== -1 && sofaChooserDismissed) {
      setSofaChooserDismissed(false);
    }
  }, [curType, sofaChooserOpen, sofaChooserDismissed, typesData]);
  useEffect(() => {
    (window as any).reeShareLinkRestore = async () => {
      if (
        materialsData === undefined ||
        elementsData === undefined ||
        typesData === undefined
      ) {
        throw Error();
      }
      (window as any).eneIsShareLink = false;
      const params = new URLSearchParams(window.location.search);
      const stateAlpha = params.get("state");
      if (stateAlpha === null) {
        throw Error();
      }
      const state = JSON.parse(atob(stateAlpha)) as UrlStateParam;
      const currentTypeObject = typesData.data.find(
        (it) => it.id === state.type
      );
      if (currentTypeObject === undefined) {
        throw Error();
      }
      await onTypeClick(currentTypeObject, true);
      setCurOptions(state.options ?? []);
      const event = {
        elements: state.elements.map((element) => {
          const curElement = elementsData.data.find(
            (it) => it.id === element.i
          );
          if (curElement === undefined) {
            throw Error();
          }
          let colorExternalId: string | undefined = undefined;
          for (const material of materialsData.data) {
            for (const color of material.colors) {
              if (color.id === element.c) {
                colorExternalId = color.external_id;
                break;
              }
            }
          }
          if (colorExternalId === undefined) {
            throw Error();
          }
          const curState = curElement.states.find((it) => it.id === element.s);
          const res: RestoreCurrentElement = {
            ...element,
            r: element.r2 === undefined ? element.r : -1,
            r2: element.r2 ?? -1,
            external_id: curElement.external_id,
            color_external_id: colorExternalId,
            s: element.s ?? -1,
            state_external_id: curState?.external_id ?? "",
          };
          return res;
        }),
        options: curOptionsToExternal(state.options ?? [], currentTypeObject),
      };
      (window as any).reeWasRestored = true;
      await client.request("SofaRestore", event);
      updateRoomDimensions();
    };
  }, [materialsData, elementsData, typesData]);
  useEffect(() => {
    window.addEventListener("storage", (event) => {
      setUserTypes(getUserTypesFromLocalStorage());
    });
  }, []);
  useEffect(() => {
    const getMaterials = async () => {
      const response: MaterialsData = await fetch(
        `${config.apiUrl}/items/sofa_materials?fields=*,colors.*,sofa_types.sofa_types_id`
      ).then((response) => response.json());
      setMaterialsData(response);
      const newCurColors: { [key: number]: number } = {};
      const materials = response as MaterialsData;
      for (const material of materials.data) {
        newCurColors[material.id] = material.colors[0].id;
      }
      setCurColors(newCurColors);
      setCurMaterial(materials.data[0].id);
    };
    getMaterials();
    const getPresets = async () => {
      const response: TypesData = await fetch(
        `${config.apiUrl}/items/sofa_types?fields=*,translations.*,presets.*,presets.translations.*,options.sofa_options_groups_id.*,options.sofa_options_groups_id.translations.*,options.sofa_options_groups_id.options.*,options.sofa_options_groups_id.options.translations.*&sort=sort`
      ).then((response) => response.json());
      console.log(response);
      if (params.get("adminKey") === null) {
        response.data = response.data.filter(
          (it) => it.only_show_in_admin !== true
        );
      }
      for (const type of response.data) {
        type.presets.sort((it, it2) => it.sort - it2.sort);
      }
      setTypesData(response);
    };
    getPresets();
    const getElements = async () => {
      const response: ElementsData = await fetch(
        `${config.apiUrl}/items/sofa_elements?fields=*,translations.*,states.*,states.translations.*`
      ).then((response) => response.json());
      console.log(response);
      setElementsData(response);
    };
    getElements();
  }, []);
  useEffect(() => {
    (window as any).sofa_render_finished = function (action: {
      filename: string;
    }) {
      console.log("SOFA RENDER FINISHED :)", action);
      const filesDirUrl =
        config.filesServerOverride ?? (window as any).reeFilesUrl;
      if (filesDirUrl === undefined) {
        throw Error();
      }
      const fileUrl = `${filesDirUrl}/${action.filename}`;
      fetch(fileUrl, {})
        .then((res) => res.blob())
        .then((blob) => {
          if (blob != null) {
            var url = window.URL.createObjectURL(blob);
            var a = document.createElement("a");
            a.href = url;
            a.download = action.filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }
        });
    };
  }, []);
  useEffect(() => {
    (window as any).sofa_elements_data_update = function (
      action: SofaElementsUpdateAction
    ) {
      console.log("DATA UPDATE");
      const extWindow = window as any;
      if (extWindow.reeWasRestored === true) {
        delete extWindow.reeWasRestored;
        console.log("IGNORING THIS CALL");
      } else if (!action.save_change) {
        console.log("INGORING THIS CALL: save_change === false");
      } else {
        const curPreset = curPresets[curType];
        if (curPreset === undefined) {
          console.error();
          return;
        }
        if (curPreset.isUserPreset) {
          const newUserTypes = { ...userTypes };
          const newUserType = newUserTypes[curType];
          if (newUserType === undefined) {
            console.error();
          }
          const currentUserPreset = newUserType.presets.find(
            (it) => it.id === curPreset.id
          );
          if (currentUserPreset === undefined) {
            console.error();
            return;
          }
          currentUserPreset.elements = action.elements;
          setUserTypes(newUserTypes);
        } else {
          if (extWindow.reeWasChangeColorAll === true) {
            delete extWindow.reeWasChangeColorAll;
            console.log("IGNORING THIS CALL 2");
          } else {
            const newUserTypes = { ...userTypes };
            let newUserType = newUserTypes[curType];
            if (newUserType === undefined) {
              newUserType = { maxCount: 0, presets: [] };
              newUserTypes[curType] = newUserType;
            }
            ++newUserType.maxCount;
            const currentTypeObject = typesData?.data.find(
              (it) => it.id === curType
            );
            if (currentTypeObject === undefined) {
              console.error();
              return;
            }
            const newId = Date.now();
            newUserType.presets.push({
              id: newId,
              name:
                _("My") +
                ` ${dynTr(currentTypeObject.translations, (it) => it.name)} ${
                  newUserTypes[curType].maxCount
                }`,
              elements: action.elements,
            });
            setUserTypes(newUserTypes);
            const newCurPresets = { ...curPresets };
            newCurPresets[curType] = { isUserPreset: true, id: newId };
            setCurPresets(newCurPresets);
          }
        }
      }
      console.log("Saving elements to:", action);
      setCurElements(action);
    };
  }, [dynTr, curType, curPresets, curElements, userTypes, typesData, _]);
  const curPreset = curPresets[curType];
  const curTypeObj = typesData?.data.find((it) => it.id === curType);
  const curPresetName = (() => {
    if (curPreset === undefined || curTypeObj === undefined) {
      return undefined;
    }
    if (curPreset.isUserPreset) {
      return userTypes[curType]?.presets.find((it) => it.id === curPreset.id)
        ?.name;
    }
    const preset = curTypeObj.presets.find((it) => it.id === curPreset.id);
    return preset ? dynTr(preset.translations, (it) => it.name) : undefined;
  })();
  const onPresetClick = async (
    preset: SofaPreset,
    curTypeReal: number,
    curOptionsReal: Array<{ groupId: number; optionId: number }>,
    curMaterialReal: number,
    fromRestore: boolean = false
  ) => {
    if (!fromRestore && eneIsLoading()) {
      return;
    }
    setCurPresets({
      ...curPresets,
      [curTypeReal]: {
        isUserPreset: false,
        id: preset.id,
      },
    });
    const elementPositions = JSON.parse(
      preset.element_positions
    ) as CurrentElement[];
    const curColor = materialsData?.data
      ?.find((it) => it.id === curMaterialReal)
      ?.colors.find((it) => it.id === curColors[curMaterialReal]);
    if (curColor === undefined) {
      throw Error();
    }
    const currentTypeObject = typesData?.data.find(
      (it) => it.id === curTypeReal
    );
    if (currentTypeObject === undefined) {
      throw Error();
    }
    const extWindow = window as any;
    extWindow.reeWasRestored = true;
    await client.request("SofaRestore", {
      elements: elementPositions.map((element) => {
        const curElement = elementsData?.data.find((it) => it.id === element.i);
        if (curElement === undefined) {
          throw Error();
        }
        const curState = curElement.states.find((it) => it.id === element.s);
        return {
          ...element,
          r: element.r2 === undefined ? element.r : -1,
          r2: element.r2 ?? -1,
          external_id: curElement.external_id,
          color_external_id: curColor.external_id,
          c: curColor.id,
          s: element.s ?? -1,
          state_external_id: curState?.external_id ?? "",
        };
      }),
      options: curOptionsToExternal(curOptionsReal, currentTypeObject),
    });
    updateRoomDimensions();
  };
  const onUserPresetClick = async (
    userPreset: UserPreset,
    curTypeReal: number,
    curOptionsReal: Array<{ groupId: number; optionId: number }>,
    fromRestore: boolean = false
  ) => {
    if (!fromRestore && eneIsLoading()) {
      return;
    }
    const currentTypeObject = typesData?.data.find(
      (it) => it.id === curTypeReal
    );
    if (currentTypeObject === undefined) {
      throw Error();
    }
    const event = {
      elements: userPreset.elements.map((element) => {
        const curElement = elementsData?.data.find((it) => it.id === element.i);
        if (curElement === undefined) {
          throw Error();
        }
        let colorExternalId: string | undefined = undefined;
        for (const material of materialsData?.data ?? []) {
          for (const color of material.colors) {
            if (color.id === element.c) {
              colorExternalId = color.external_id;
              break;
            }
          }
        }
        if (colorExternalId === undefined) {
          throw Error();
        }

        const curState = curElement.states.find((it) => it.id === element.s);
        let res: RestoreCurrentElement = {
          ...element,
          r: element.r2 === undefined ? element.r : -1,
          r2: element.r2 ?? -1,
          external_id: curElement.external_id,
          color_external_id: colorExternalId,
          s: element.s ?? -1,
          state_external_id: curState?.external_id ?? "",
        };
        return res;
      }),
      options: curOptionsToExternal(curOptionsReal, currentTypeObject),
    };
    (window as any).reeWasRestored = true;
    await client.request("SofaRestore", event);
    setCurPresets({
      ...curPresets,
      [curTypeReal]: {
        isUserPreset: true,
        id: userPreset.id,
      },
    });
    updateRoomDimensions();
  };
  const buildDefaultOptions = (type: SofaType) => {
    const newOptions: Array<{ groupId: number; optionId: number }> = [];
    for (const it of type.options) {
      const optionsGroup = it.sofa_options_groups_id;
      if (optionsGroup.options.length > 0) {
        newOptions.push({
          groupId: optionsGroup.id,
          optionId: optionsGroup.options[0].id,
        });
      }
    }
    return newOptions;
  };
  const onTypeClick = async (
    type: SofaType,
    fromRestore: boolean = false,
    overrideOptions:
      | Array<{ groupId: number; optionId: number }>
      | undefined = undefined
  ) => {
    if (!fromRestore && eneIsLoading()) {
      return;
    }
    const newOptions = overrideOptions ?? [];
    if (overrideOptions === undefined) {
      for (let it of type.options) {
        const optionsGroup = it.sofa_options_groups_id;
        if (optionsGroup.options.length > 0) {
          newOptions.push({
            groupId: optionsGroup.id,
            optionId: optionsGroup.options[0].id,
          });
        }
      }
    }
    setCurOptions(newOptions);
    let curMaterialReal = curMaterial;
    if (curMaterial !== -1 && materialsData !== undefined) {
      const filtered = materialsData.data.filter(
        (it) =>
          it.sofa_types.findIndex((it2) => it2.sofa_types_id === type.id) !== -1
      );
      if (filtered.findIndex((it) => it.id === curMaterial) === -1) {
        setCurMaterial(filtered[0].id);
        curMaterialReal = filtered[0].id;
      }
    }
    setCurType(type.id);
    const currentTypeObject = typesData?.data.find((it) => it.id === type.id);
    if (currentTypeObject === undefined) {
      console.error();
      return;
    }
    const curPreset = curPresets[type.id];
    if (curPreset === undefined) {
      onPresetClick(
        currentTypeObject.presets[0],
        type.id,
        newOptions,
        curMaterialReal,
        fromRestore
      );
    } else {
      if (curPreset.isUserPreset) {
        const userType = userTypes[type.id];
        if (userType === undefined) {
          console.error();
          return;
        }
        const currentUserPreset = userType.presets.find(
          (it) => it.id === curPreset.id
        );
        if (currentUserPreset === undefined) {
          console.error();
          return;
        }
        onUserPresetClick(currentUserPreset, type.id, newOptions, fromRestore);
      } else {
        const currentPreset = currentTypeObject.presets.find(
          (it) => it.id === curPreset.id
        );
        if (currentPreset === undefined) {
          console.error();
          return;
        }
        onPresetClick(
          currentPreset,
          type.id,
          newOptions,
          curMaterialReal,
          fromRestore
        );
      }
    }
  };
  useEffect(() => {
    (window as any).reeLevelRestore = async function () {
      const currentTypeObject = typesData?.data.find((it) => it.id === curType);
      if (currentTypeObject !== undefined) {
        // This is wake up from sleep
        await onTypeClick(currentTypeObject, true, curOptions);
      } else if ((window as any).eneIsShareLink) {
        // New instance from share link
        await (window as any).reeShareLinkRestore();
      }
      await (window as any).reeActionBarRestore();
    };
  }, [onTypeClick]);
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
      }}
    >
      {tutorialEnabled && typesData !== undefined && showUserGuide && (
        <UserGuide curType={curType} setShowUserGuide={setShowUserGuide} />
      )}
      <Dialog
        open={pendingUserPresetDelete !== undefined}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {_("Delete your customized sofa?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {_("Do you really wish to delete your customized sofa set") +
              ' "' +
              (pendingUserPresetDelete?.preset.name ?? "") +
              '"'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingUserPresetDelete(undefined)}>
            {_("Cancel")}
          </Button>
          <Button
            onClick={() => {
              const pending = pendingUserPresetDelete;
              if (pending === undefined) {
                console.error();
                return;
              }
              const { preset: userPreset, typeId } = pending;
              const newUserTypes = { ...userTypes };
              const newUserType = newUserTypes[typeId];
              if (newUserType === undefined) {
                console.error();
              }
              const presetIndex = newUserType.presets.findIndex(
                (it) => it.id === userPreset.id
              );
              if (presetIndex === -1) {
                console.error();
                return;
              }
              newUserType.presets.splice(presetIndex, 1);
              setUserTypes(newUserTypes);
              if (typeId === curType) {
                const presets = typesData?.data.find(
                  (it) => it.id === curType
                )?.presets;
                if (presets === undefined || presets.length < 1) {
                  console.error();
                  return;
                }
                onPresetClick(presets[0], curType, curOptions, curMaterial);
              }
              setPendingUserPresetDelete(undefined);
            }}
          >
            {_("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={myDesignsOpen}
        onClose={() => setMyDesignsOpen(false)}
        fullScreen={fullScreenChooser}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {gTxt("user_preset_button_title") || _("My Designs")}
            <IconButton
              aria-label={_("Close")}
              onClick={() => setMyDesignsOpen(false)}
              sx={{ position: "absolute", right: 0 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {typesData?.data
            .filter((type) => (userTypes[type.id]?.presets ?? []).length > 0)
            .map((type) => (
              <Box key={type.id} sx={{ marginBottom: "16px" }}>
                <Typography
                  sx={{
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  {dynTr(type.translations, (it) => it.name)}
                </Typography>
                {(userTypes[type.id]?.presets ?? []).map((preset) => (
                  <Box
                    key={preset.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 0",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Typography>{preset.name}</Typography>
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (typesData === undefined) {
                            return;
                          }
                          const newOptions = buildDefaultOptions(type);
                          setCurOptions(newOptions);
                          setCurType(type.id);
                          onUserPresetClick(preset, type.id, newOptions);
                          setMyDesignsOpen(false);
                        }}
                      >
                        {_("View")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          setPendingUserPresetDelete({
                            preset,
                            typeId: type.id,
                          })
                        }
                      >
                        {_("Delete")}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          {typesData !== undefined &&
            typesData.data.filter(
              (type) => (userTypes[type.id]?.presets ?? []).length > 0
            ).length === 0 && (
              <Typography>
                {gTxt("user_preset_popup_empty") || _("No saved designs")}
              </Typography>
            )}
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          pointerEvents: "initial",
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          "@media (orientation: portrait)": {
            right: 0,
          },
          "@media (orientation: landscape)": {
            right: curType === -1 ? "0" : { xs: "250px", lg: "350px", xl: "500px" },
          },
          [vB.xs]: {
            height: topBarSmall,
          },
          [vB.lg]: {
            height: largerThen750 ? topBarLarge : topBarSmall,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {isVbLg && largerThen750 && (
          <Box
            component="a"
            href={dynTr(props.space.translations, (it) => it.logo_link)}
            onDragStart={(e) => e.preventDefault()}
            sx={{ marginLeft: "20px", marginRight: "auto", marginY: "8px" }}
          >
            <Box
              component="img"
              onDragStart={(e) => e.preventDefault()}
              sx={{
                height: { xs: isXl ? "80px" : "56px", lg: isXl ? "80px" : "56px" },
              }}
              src={
                props.space.logo
                  ? `${config.apiUrl}/assets/${props.space.logo}`
                  : `imgs/${config.spaceId}_logo.svg`
              }
            />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "20px", gap: "10px" }}>
          <Button
            variant="contained"
            sx={{
              ...chooseSofaButtonSx,
            }}
            onClick={() => setMyDesignsOpen(true)}
          >
            {gTxt("user_preset_button_title") || _("My Designs")}
          </Button>
          <Button
            variant="contained"
            sx={{
              ...chooseSofaButtonSx,
            }}
            onClick={() => {
              setSofaChooserDismissed(false);
              setSofaChooserType(-1);
              setSofaChooserOpen(true);
            }}
          >
            {gTxt("preset_button_title") || _("Choose preset")}
          </Button>
        </Box>
      </Box>
      <Dialog
        open={sofaChooserOpen}
        onClose={() => {
          setSofaChooserOpen(false);
          setSofaChooserDismissed(true);
        }}
        fullScreen={fullScreenChooser}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: fullScreenChooser
            ? {
                position: "relative",
                width: "100vw",
                height: "100vh",
                maxWidth: "100vw",
                maxHeight: "100vh",
                margin: 0,
                borderRadius: 0,
                overflowX: "hidden",
                paddingTop: "calc(var(--sat) + 60px)",
              }
            : {
                position: "relative",
                width: "80vw",
                height: "80vh",
                maxWidth: "80vw",
                maxHeight: "80vh",
                overflowX: "hidden",
              },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "1.6rem",
            fontWeight: 600,
            paddingTop: "24px",
            "@media (max-width:900px)": {
              fontSize: "1.3rem",
              paddingTop: "16px",
            },
            "@media (max-width:600px)": {
              fontSize: "0.95rem",
              paddingTop: "calc(12px + var(--sat))",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingLeft: "48px",
              paddingRight: "48px",
              "@media (max-width:600px)": {
                paddingLeft: "44px",
                paddingRight: "44px",
              },
            }}
          >
            {sofaChooserType !== -1 && (
              <Button
                className="ree-ug-back-to-types-button"
                onClick={() => setSofaChooserType(-1)}
                startIcon={<ArrowBackIosNewIcon />}
                variant="outlined"
                sx={{
                  position: "absolute",
                  left: 0,
                  ...backButtonSx,
                  "@media (max-width:600px)": {
                    minWidth: "auto",
                    padding: "6px 12px",
                    minHeight: "36px",
                    fontSize: "0.7em",
                  },
                }}
              >
                {_("Back")}
              </Button>
            )}
            <IconButton
              aria-label={_("Close")}
              onClick={() => {
                setSofaChooserOpen(false);
                setSofaChooserDismissed(true);
              }}
              sx={{
                position: "absolute",
                right: 0,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="span"
              sx={{
                whiteSpace: "normal",
                lineHeight: 1.2,
              }}
            >
              {sofaChooserType === -1
                ? gTxt("preset_popup_title_1") || _("Choose type")
                : gTxt("preset_popup_title_2") || _("Choose preset")}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "24px !important",
            overflowX: "hidden",
            "@media (max-width:900px)": {
              padding: "16px !important",
            },
            "@media (max-width:600px)": {
              padding: "12px !important",
            },
          }}
        >
          <PresetChooser
            typesData={typesData}
            userTypes={userTypes}
            curType={curType}
            activeType={sofaChooserType}
            curPreset={curPreset}
            onTypeClick={(type) => {
              onTypeClick(type);
              setSofaChooserType(type.id);
            }}
            onPresetClick={(preset) => {
              const targetType =
                sofaChooserType === -1 ? curType : sofaChooserType;
              onPresetClick(preset, targetType, curOptions, curMaterial);
              setSofaChooserOpen(false);
            }}
            onUserPresetClick={(userPreset) => {
              const targetType =
                sofaChooserType === -1 ? curType : sofaChooserType;
              onUserPresetClick(userPreset, targetType, curOptions);
              setSofaChooserOpen(false);
            }}
            onUserPresetDelete={(userPreset) =>
              setPendingUserPresetDelete({
                preset: userPreset,
                typeId: sofaChooserType === -1 ? curType : sofaChooserType,
              })
            }
            onTypesButtonClick={() => setSofaChooserType(-1)}
            showCurrentElements={() => {
              setShowingCurElements(true);
            }}
            space={props.space}
            layout="popup"
            showHeader={params.get("adminKey") !== null}
            showBackButton={false}
          />
        </DialogContent>
        {props.loadingLevel && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              background:
                "radial-gradient(circle at top, rgba(255,255,255,0.98), rgba(255,255,255,0.88) 45%, rgba(245,245,245,0.82) 100%)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 2.5, md: 3 },
              pointerEvents: "auto",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: { xs: "180px", md: "260px" },
                height: { xs: "180px", md: "260px" },
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 38%, rgba(0,0,0,0) 70%)",
                filter: "blur(2px)",
                animation: "ree-pulse 1800ms ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: { xs: 92, md: 128 },
                height: { xs: 92, md: 128 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes ree-spin": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "100%": {
                    transform: "rotate(360deg)",
                  },
                },
                "@keyframes ree-pulse": {
                  "0%, 100%": {
                    transform: "scale(0.92)",
                    opacity: 0.65,
                  },
                  "50%": {
                    transform: "scale(1.08)",
                    opacity: 1,
                  },
                },
                "@keyframes ree-bob": {
                  "0%, 100%": {
                    transform: "translateY(0px)",
                  },
                  "50%": {
                    transform: "translateY(-5px)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "3px solid rgba(0,0,0,0.08)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "5px solid transparent",
                  borderTopColor: "#111",
                  borderRightColor: "#111",
                  animation: "ree-spin 1s linear infinite",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: { xs: 12, md: 16 },
                  borderRadius: "50%",
                  border: "4px solid transparent",
                  borderBottomColor: "#8f8f8f",
                  borderLeftColor: "#8f8f8f",
                  animation: "ree-spin 1.6s linear infinite reverse",
                }}
              />
              <Box
                sx={{
                  width: { xs: 20, md: 28 },
                  height: { xs: 20, md: 28 },
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle at 30% 30%, #ffffff, #d8d8d8 45%, #111 100%)",
                  boxShadow: "0 10px 24px rgba(0, 0, 0, 0.18)",
                  animation: "ree-bob 1.4s ease-in-out infinite",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: { xs: "1.35rem", md: "2rem" },
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "#111",
                textAlign: "center",
                paddingX: 2,
                textShadow: "0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              {_("Loading 3D view...")}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: { xs: "0.85rem", md: "1rem" },
                fontWeight: 500,
                color: "#555",
                textAlign: "center",
                maxWidth: "26ch",
                paddingX: 3,
              }}
            >
              {_("Preparing your scene.")}
            </Typography>
          </Box>
        )}
      </Dialog>
      <Dialog open={showingCurElements}>
        <DialogTitle>Current elements list</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {JSON.stringify(curElements.elements)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowingCurElements(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      <ContactForm
        open={contactFormOpen}
        setOpen={setContactFormOpen}
        curElements={curElements}
      />
      <Sidebar
        setLoading={props.setLoading}
        materialsData={materialsData}
        curMaterial={curMaterial}
        setCurMaterial={setCurMaterial}
        curColors={curColors}
        setCurColors={setCurColors}
        typesData={typesData}
        curType={curType}
        curPreset={curPreset}
        curElements={curElements}
        elementsData={elementsData}
        userTypes={userTypes}
        setUserTypes={setUserTypes}
        curOptions={curOptions}
        setCurOptions={setCurOptions}
        contactFormOpen={contactFormOpen}
        setContactFormOpen={setContactFormOpen}
        space={props.space}
      />
      <ElementDetails
        elementsData={elementsData}
        materialsData={materialsData}
        curMaterial={curMaterial}
        curColors={curColors}
        curElements={curElements}
        curType={curType}
      />
      <ActionBar
        updateRoomDimensions={updateRoomDimensions}
        roomSizeX={roomSizeX}
        roomSizeY={roomSizeY}
        roomHardWalls={roomHardWalls}
        showMeasures={showMeasures}
        setShowMeasures={setShowMeasures}
        advanced3D={advanced3D}
        setAdvanced3D={setAdvanced3D}
        disableModuleMovements={disableModuleMovements}
        setDisableModuleMovements={setDisableModuleMovements}
      />
      {showMeasures && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            padding: "10px 0",
            textAlign: "center",
            "@media (orientation: landscape)": {
              bottom: 0,
              width: {
                xs: "calc(100vw - 250px)",
                lg: "calc(100vw - 350px)",
                xl: "calc(100vw - 500px)",
              },
            },
            "@media (orientation: portrait)": {
              bottom: "calc(30% + 50px)",
              width: "100vw",
            },
            backgroundColor: "#fff8",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            {_(
              "Because our products are hand made the dimensions can be ±3%% different than shown above."
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
